import { useCallback } from "react";
import { directUploadWithProgress, updateMediaAltText } from "@core/features/uploadFile";
import type { FileType } from "@core/types/file.types";
import { FileAnswerInput, type Props as FileAnswerInputProps } from "./FileAnswerInput";

export type Props = Omit<FileAnswerInputProps, "uploadFileHandler" | "onAltTextChange">;

/**
 * Container for FileAnswerInput.
 * Provides upload handler that integrates with the S3 direct upload system.
 *
 * This container handles:
 * - S3 direct upload via presigned URLs
 * - Progress tracking
 * - MediaUpload record creation
 *
 * @example
 * <FileAnswerInputContainer
 *   name="question-123"
 *   value={[1, 2, 3]}
 *   onChange={(ids) => console.log(ids)}
 *   onBlur={() => {}}
 *   questionType="image"
 * />
 */
export function FileAnswerInputContainer({
  questionType,
  ...props
}: Props) {
  const uploadFileHandler = useCallback(
    async (
      file: File,
      onProgress: (progress: number) => void
    ): Promise<Partial<FileType>> => {
      const mediaObject = await directUploadWithProgress(
        file,
        { file_type: questionType === "image" ? "image" : "document" },
        onProgress
      );

      // Map the media upload response to FileType format
      return {
        id: mediaObject.media_id,
        name: mediaObject.name || file.name,
        size: String(mediaObject.size || file.size),
        file: mediaObject.file_url,
        altText: mediaObject.alt_text || "",
      };
    },
    [questionType]
  );

  const handleAltTextChange = useCallback(
    async (mediaId: number, altText: string) => {
      try {
        await updateMediaAltText(mediaId, altText);
      } catch (error) {
        console.error("Failed to update alt text:", error);
      }
    },
    []
  );

  return (
    <FileAnswerInput
      {...props}
      questionType={questionType}
      uploadFileHandler={uploadFileHandler}
      onAltTextChange={handleAltTextChange}
    />
  );
}

export default FileAnswerInputContainer;
